import {
  updateLeadFollowStatus,
  updatePrivateLeadStatus,
  type ReplyContent,
} from '../api';
import {
  IPC,
  type DouyinImBrowserFlowResult,
} from '../../common/ipc/channels';
import type { DouyinImFlowResultStatus } from '../../common/enums/douyinImFlowStatus';
import { useAppStore } from './index';
import { runDouyinImTaskWithAccount } from './douyinIm';
import { getSystemShopId } from '../utils/shopContext';
import {
  buildImSessionDouyinImTaskKey,
  buildLeadDouyinImTaskKey,
  buildPrivateCommentLeadDouyinImTaskKey,
  type DouyinImTaskQueueItem,
  type DouyinImTaskQueueMap,
} from './douyinImTaskQueueState';

interface ExecuteLeadDouyinImTaskOptions {
  leadId: number;
  ecomAccountId: string;
  targetUrl: string;
  message: string;
  replyContent?: ReplyContent | null;
  showWindow?: boolean;
}

interface ExecuteImSessionDouyinImTaskOptions {
  imSessionId: string;
  ecomAccountId: string;
  targetUrl: string;
  message: string;
  replyContent?: ReplyContent | null;
  showWindow?: boolean;
}

interface ExecutePrivateCommentDouyinImTaskOptions {
  leadId: string;
  ecomAccountId: string;
  targetUrl: string;
  message: string;
  replyContent?: ReplyContent | null;
  showWindow?: boolean;
}

export interface ExecuteDouyinImTaskResult {
  flowResult: DouyinImBrowserFlowResult;
  failureReason: string;
  statusSyncErrorMessage: string;
}

const taskSyncRequests = new Map<string, Promise<string>>();

function buildSyntheticFlowResult(
  targetUrl: string,
  showWindow: boolean,
  errorMessage: string,
  resultStatus: DouyinImFlowResultStatus = 'FAILED_FLOW'
): DouyinImBrowserFlowResult {
  return {
    success: false,
    resultStatus,
    targetUrl,
    showWindow,
    pageUrl: '',
    pageTitle: '',
    cookieCount: 0,
    buttonClicked: false,
    panelReady: false,
    messageFilled: false,
    sendTriggered: false,
    verificationWaited: false,
    createCaptureCount: 0,
    sendCaptureCount: 0,
    uiSendStatus: 'unknown',
    uiMatchedMessageCount: 0,
    uiFailureText: '',
    logs: [],
    cookieSource: '',
    error: errorMessage,
  };
}

function buildInterruptedReason() {
  return '跟进失败，请重新跟进';
}

function replaceTaskQueue(tasks: DouyinImTaskQueueMap) {
  useAppStore.getState().replaceDouyinImTaskQueue(tasks);
}

async function fetchMainTaskQueueSnapshot() {
  if (!window.electronAPI) {
    return {} as DouyinImTaskQueueMap;
  }

  return window.electronAPI.invoke(IPC.GET_DOUYIN_IM_TASK_QUEUE) as Promise<DouyinImTaskQueueMap>;
}

async function updateMainTaskSyncStatus(
  taskKey: string,
  status: 'SYNC_SUCCEEDED' | 'SYNC_FAILED',
  syncErrorMessage = ''
) {
  if (!window.electronAPI) {
    return null;
  }

  return window.electronAPI.invoke(IPC.UPDATE_DOUYIN_IM_TASK_SYNC_STATUS, {
    taskKey,
    status,
    syncErrorMessage,
  }) as Promise<DouyinImTaskQueueItem | null>;
}

export async function removeDouyinImTaskFromMainQueue(taskKey: string) {
  if (!window.electronAPI) {
    return false;
  }

  const result = await window.electronAPI.invoke(IPC.REMOVE_DOUYIN_IM_TASK, { taskKey }) as { success?: boolean };
  return Boolean(result?.success);
}

function getTaskFailureReason(flowResult: DouyinImBrowserFlowResult) {
  return flowResult.uiFailureText?.trim() || flowResult.error?.trim() || '私信发送失败';
}

function serializeTaskReplyContent(replyContent?: ReplyContent | null) {
  if (!replyContent) {
    return '';
  }

  try {
    return JSON.stringify(replyContent);
  } catch (error) {
    console.warn('[DouyinIM][TaskQueue] Failed to serialize reply content.', error);
    return '';
  }
}

function resolveTaskReplyContent(task: DouyinImTaskQueueItem): ReplyContent | undefined {
  const rawReplyContent = task.replyContentJson?.trim() || '';
  if (rawReplyContent) {
    try {
      const parsed = JSON.parse(rawReplyContent) as ReplyContent;
      if (parsed && typeof parsed === 'object' && typeof parsed.type === 'string') {
        return parsed;
      }
    } catch (error) {
      console.warn('[DouyinIM][TaskQueue] Failed to parse reply content.', error);
    }
  }

  const messageText = task.message.trim();
  if (!messageText) {
    return undefined;
  }

  return {
    type: 'TEXT',
    text: messageText,
  };
}

function shouldSkipPrivateLeadStatusSync() {
  const { currentShopId, shopContexts } = useAppStore.getState();
  const normalizedShopId = currentShopId.trim();
  if (!normalizedShopId) {
    return true;
  }

  const currentShop = shopContexts.find((item) => getSystemShopId(item) === normalizedShopId);
  // 店铺上下文尚未加载完成时不做拦截，避免把未知状态误判成未绑定抖音号。
  if (!currentShop) {
    return false;
  }

  return (currentShop.douyin_accounts || []).length === 0;
}

async function syncLeadTask(task: DouyinImTaskQueueItem) {
  const replyContent = task.resultSuccess ? resolveTaskReplyContent(task) : undefined;
  await updateLeadFollowStatus({
    id: Number(task.entityId),
    ecomAccountId: task.ecomAccountId,
    followStatus: task.resultSuccess ? 'FOLLOWED' : 'FAILED',
    failureReason: task.resultSuccess ? undefined : task.failureReason,
    replyContent,
  });
}

async function syncImSessionTask(task: DouyinImTaskQueueItem) {
  if (shouldSkipPrivateLeadStatusSync()) {
    return;
  }

  const replyContent = task.resultSuccess ? resolveTaskReplyContent(task) : undefined;
  await updatePrivateLeadStatus({
    leadType: 'IM',
    leadId: task.entityId,
    leadStatus: task.resultSuccess ? 'FOLLOWED' : 'FAILED',
    failureReason: task.resultSuccess ? undefined : task.failureReason,
    replyContent,
  });
}

async function syncPrivateCommentLeadTask(task: DouyinImTaskQueueItem) {
  if (shouldSkipPrivateLeadStatusSync()) {
    return;
  }

  const replyContent = task.resultSuccess ? resolveTaskReplyContent(task) : undefined;
  await updatePrivateLeadStatus({
    leadType: 'COMMENT',
    leadId: task.entityId,
    leadStatus: task.resultSuccess ? 'FOLLOWED' : 'FAILED',
    failureReason: task.resultSuccess ? undefined : task.failureReason,
    replyContent,
  });
}

async function syncTaskResult(task: DouyinImTaskQueueItem) {
  if (task.taskKind === 'PUBLIC_LEAD') {
    await syncLeadTask(task);
    return;
  }

  if (task.taskKind === 'PRIVATE_COMMENT_LEAD') {
    await syncPrivateCommentLeadTask(task);
    return;
  }

  await syncImSessionTask(task);
}

async function syncTaskAndPersist(task: DouyinImTaskQueueItem) {
  const existingRequest = taskSyncRequests.get(task.taskKey);
  if (existingRequest) {
    return existingRequest;
  }

  const nextRequest = (async () => {
    try {
      await syncTaskResult(task);
      await updateMainTaskSyncStatus(task.taskKey, 'SYNC_SUCCEEDED');
      return '';
    } catch (error) {
      const syncErrorMessage = error instanceof Error ? error.message : '结果同步失败';
      await updateMainTaskSyncStatus(task.taskKey, 'SYNC_FAILED', syncErrorMessage);
      return syncErrorMessage;
    } finally {
      taskSyncRequests.delete(task.taskKey);
    }
  })();

  taskSyncRequests.set(task.taskKey, nextRequest);
  return nextRequest;
}

async function syncPendingTasks(
  queue: DouyinImTaskQueueMap,
  options: {
    taskKeys?: string[];
    includeSyncFailed?: boolean;
  } = {}
) {
  const wantedTaskKeys = options.taskKeys ? new Set(options.taskKeys) : null;
  const syncRetryTasks = Object.values(queue).filter((task) => {
    if (wantedTaskKeys && !wantedTaskKeys.has(task.taskKey)) {
      return false;
    }

    return task.status === 'RESULT_PENDING_SYNC' || (options.includeSyncFailed && task.status === 'SYNC_FAILED');
  });

  await Promise.all(syncRetryTasks.map(async (task) => {
    await syncTaskAndPersist(task);
  }));
}

async function applyQueueSnapshot(
  queue: DouyinImTaskQueueMap,
  options: {
    includeSyncFailed?: boolean;
  } = {}
) {
  replaceTaskQueue(queue);
  await syncPendingTasks(queue, {
    includeSyncFailed: options.includeSyncFailed,
  });
}

export async function refreshDouyinImTaskQueueFromMain(
  options: {
    includeSyncFailed?: boolean;
  } = {}
) {
  const queue = await fetchMainTaskQueueSnapshot();
  await applyQueueSnapshot(queue, options);
  return queue;
}

export function setupDouyinImTaskQueueBridge() {
  if (!window.electronAPI) {
    replaceTaskQueue({});
    return () => {};
  }

  const cleanup = window.electronAPI.on(IPC.DOUYIN_IM_TASK_QUEUE_UPDATED, (payload) => {
    void applyQueueSnapshot(payload as DouyinImTaskQueueMap);
  });

  void refreshDouyinImTaskQueueFromMain({ includeSyncFailed: true });

  return () => {
    cleanup();
  };
}

async function executeTaskWithMainQueue(
  taskKey: string,
  options: {
    taskKind: 'PUBLIC_LEAD' | 'IM_SESSION' | 'PRIVATE_COMMENT_LEAD';
    entityId: string;
    ecomAccountId: string;
    targetUrl: string;
    message: string;
    replyContent?: ReplyContent | null;
    showWindow?: boolean;
  }
): Promise<ExecuteDouyinImTaskResult> {
  let flowResult: DouyinImBrowserFlowResult;
  try {
    flowResult = await runDouyinImTaskWithAccount({
      taskKind: options.taskKind,
      entityId: options.entityId,
      ecomAccountId: options.ecomAccountId,
      targetUrl: options.targetUrl,
      message: options.message,
      replyContentJson: serializeTaskReplyContent(options.replyContent),
      showWindow: options.showWindow,
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '私信任务执行失败';
    flowResult = buildSyntheticFlowResult(options.targetUrl, Boolean(options.showWindow), errorMessage);
  }

  const queue = await fetchMainTaskQueueSnapshot();
  replaceTaskQueue(queue);
  const targetTask = queue[taskKey];
  if (targetTask && (targetTask.status === 'RESULT_PENDING_SYNC' || targetTask.status === 'SYNC_FAILED')) {
    await syncPendingTasks(queue, {
      taskKeys: [taskKey],
      includeSyncFailed: true,
    });
  }

  const latestQueue = await fetchMainTaskQueueSnapshot();
  replaceTaskQueue(latestQueue);
  const latestTask = latestQueue[taskKey];

  return {
    flowResult,
    failureReason: latestTask?.failureReason || getTaskFailureReason(flowResult),
    statusSyncErrorMessage: latestTask?.syncErrorMessage || '',
  };
}

export async function executeLeadDouyinImTask(options: ExecuteLeadDouyinImTaskOptions) {
  const taskKey = buildLeadDouyinImTaskKey(options.leadId);
  return executeTaskWithMainQueue(taskKey, {
    taskKind: 'PUBLIC_LEAD',
    entityId: String(options.leadId),
    ecomAccountId: options.ecomAccountId,
    targetUrl: options.targetUrl,
    message: options.message,
    replyContent: options.replyContent,
    showWindow: options.showWindow,
  });
}

export async function executeImSessionDouyinImTask(options: ExecuteImSessionDouyinImTaskOptions) {
  const taskKey = buildImSessionDouyinImTaskKey(options.imSessionId);
  return executeTaskWithMainQueue(taskKey, {
    taskKind: 'IM_SESSION',
    entityId: options.imSessionId,
    ecomAccountId: options.ecomAccountId,
    targetUrl: options.targetUrl,
    message: options.message,
    replyContent: options.replyContent,
    showWindow: options.showWindow,
  });
}

export async function executePrivateCommentDouyinImTask(options: ExecutePrivateCommentDouyinImTaskOptions) {
  const taskKey = buildPrivateCommentLeadDouyinImTaskKey(options.leadId);
  return executeTaskWithMainQueue(taskKey, {
    taskKind: 'PRIVATE_COMMENT_LEAD',
    entityId: options.leadId,
    ecomAccountId: options.ecomAccountId,
    targetUrl: options.targetUrl,
    message: options.message,
    replyContent: options.replyContent,
    showWindow: options.showWindow,
  });
}

export function isTaskRunning(task?: DouyinImTaskQueueItem | null) {
  return task?.status === 'RUNNING_BROWSER';
}

export function isTaskResultUncertain(task?: DouyinImTaskQueueItem | null) {
  return task?.status === 'RESULT_UNCERTAIN';
}

export function isTaskResolvedSuccess(task?: DouyinImTaskQueueItem | null) {
  return Boolean(task)
    && task.status !== 'RUNNING_BROWSER'
    && task.status !== 'RESULT_UNCERTAIN'
    && task.status !== 'INTERRUPTED'
    && task.resultSuccess;
}

export function isTaskResolvedFailure(task?: DouyinImTaskQueueItem | null) {
  return Boolean(task)
    && task.status !== 'RUNNING_BROWSER'
    && task.status !== 'RESULT_UNCERTAIN'
    && (task.status === 'INTERRUPTED' || !task.resultSuccess);
}

export function getTaskDisplayError(task?: DouyinImTaskQueueItem | null) {
  if (!task) {
    return '';
  }

  if (task.status === 'RESULT_UNCERTAIN') {
    return '';
  }

  if (task.status === 'INTERRUPTED') {
    return task.failureReason || buildInterruptedReason();
  }

  return task.failureReason || task.syncErrorMessage || '';
}
